import { Component, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AiCompanion, recentDialogue } from "./components/AiCompanion";
import type { AssistantTaskEvent, AssistantTaskEventKind } from "./components/assistantPromptState";
import { AssetImage } from "./components/AssetImage";
import { EvidenceSheet } from "./components/EvidenceSheet";
import { Hud } from "./components/Hud";
import { InteractionFeedbackProvider, type InteractionFeedbackData, type InteractionFeedbackKind } from "./components/InteractionFeedback";
import { JourneyMap } from "./components/JourneyMap";
import { PassageDrawer } from "./components/PassageDrawer";
import { QuizScreen, type QuizAssistantContext } from "./components/QuizScreen";
import { ReportScreen } from "./components/ReportScreen";
import { SettingsDialog } from "./components/SettingsDialog";
import { StaticBackdrop } from "./components/StaticBackdrop";
import { TitleScreen } from "./components/TitleScreen";
import type { ChapterInteractionProps } from "./chapters/chapterTypes";
import { chapters, getChapter } from "./content/journey";
import { getSceneDefinition, getScenesForChapter } from "./content/scenes";
import { createAssistantContextSnapshot, getAssistantGuide } from "./content/socraticGuide";
import { createSceneProgress, useProgress } from "./hooks/useProgress";
import { playUiSound, setMusicDucked, setSoundEnabled, startJourneyAudio } from "./lib/audio";
import { deriveNatureStageSnapshot } from "./scene/stageState";
import type { ChapterId, EvidenceId, MisconceptionTag, SceneId, SceneProgress, Screen, Season } from "./types";

const ChapterOne = lazy(() => import("./chapters/ChapterOne").then((module) => ({ default: module.ChapterOne })));
const ChapterTwo = lazy(() => import("./chapters/ChapterTwo").then((module) => ({ default: module.ChapterTwo })));
const ChapterThree = lazy(() => import("./chapters/ChapterThree").then((module) => ({ default: module.ChapterThree })));
const ChapterFour = lazy(() => import("./chapters/ChapterFour").then((module) => ({ default: module.ChapterFour })));
const NatureStage = lazy(() => import("./scene/NatureStage").then((module) => ({ default: module.NatureStage })));

function getSeasonForChapter(id: ChapterId): Season {
  if (id === "chapter-3") return "winter";
  if (id === "chapter-4") return "autumn";
  return "spring";
}

function supportsWebGl(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2"));
  } catch {
    return false;
  }
}

function getSavedTimelinePosition(sceneProgress?: SceneProgress): number {
  const controls = sceneProgress?.visualState.controlValues;
  const timelinePosition = controls?.timelineProgress;
  if (typeof timelinePosition === "number" && Number.isFinite(timelinePosition)) {
    return Math.max(0, Math.min(100, timelinePosition));
  }

  const stage = controls?.stage;
  return typeof stage === "number" && Number.isFinite(stage)
    ? Math.max(0, Math.min(3, stage)) / 3 * 100
    : 0;
}

function taskAttemptValueKey(value: unknown): string {
  if (value === null || value === undefined) return "none";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value) ?? "unknown";
  } catch {
    return "unknown";
  }
}

class RendererErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

type ActiveSurface = "ai" | "passage" | "evidence" | "settings" | null;

function misconceptionForScene(sceneId: SceneId): MisconceptionTag {
  const phase = getAssistantGuide(sceneId).phase;
  if (sceneId === "F02") return "scope-overreach";
  if (phase === "verify") return "evidence-missing";
  if (phase === "revise") return "expression-unclear";
  if (sceneId.startsWith("C3-")) return "causality-overstated";
  return "concept-confusion";
}

const C3_SUMMARY_RULE_EVIDENCE: Record<string, EvidenceId> = {
  "c3.summary.latitude": "P07",
  "c3.summary.longitude": "P08",
  "c3.summary.elevation": "P09",
  "c3.summary.era": "P10"
};

function isC3SummaryMissingEvidence(sceneId: SceneId, ruleId: string, viewed: EvidenceId[]): boolean {
  const evidenceId = C3_SUMMARY_RULE_EVIDENCE[ruleId];
  return sceneId === "C3-05" && Boolean(evidenceId) && !viewed.includes(evidenceId);
}

export default function App() {
  useEffect(() => {
    // 清理旧版本曾允许浏览器保存的自定义密钥；在线请求现在只使用服务端密钥。
    try { window.localStorage.removeItem("nature-language-deepseek-key-v1"); } catch {}
  }, []);
  const progressApi = useProgress();
  const [screen, setScreen] = useState<Screen>(() => progressApi.progress.route.screen);
  const [currentChapterId, setCurrentChapterId] = useState<ChapterId>(() => progressApi.progress.route.chapterId);
  const [activeSurface, setActiveSurface] = useState<ActiveSurface>(null);
  const [evidenceId, setEvidenceId] = useState<EvidenceId | null>(null);
  const [evidenceTitle, setEvidenceTitle] = useState<string | undefined>();
  const [focusEvidenceIds, setFocusEvidenceIds] = useState<string[]>([]);
  const [experimentValue, setExperimentValue] = useState(() =>
    getSavedTimelinePosition(progressApi.progress.scenes["C2-00"])
  );
  const [quizAssistantContext, setQuizAssistantContext] = useState<QuizAssistantContext | null>(null);
  const [interactionStatus, setInteractionStatus] = useState<InteractionFeedbackData | null>(null);
  const [mapCompletionNotice, setMapCompletionNotice] = useState<{ chapterId: ChapterId; message: string } | null>(null);
  const [assistantTaskEvent, setAssistantTaskEvent] = useState<AssistantTaskEvent | null>(null);
  const interactionFeedbackId = useRef(0);
  const assistantTaskEventId = useRef(0);
  const lastAssistantTaskEvent = useRef<{ signature: string; createdAt: number } | null>(null);
  const [rendererRevision, setRendererRevision] = useState(0);
  const [webGlAvailable] = useState(supportsWebGl);
  const [compactViewport, setCompactViewport] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const currentChapter = getChapter(currentChapterId);
  const season = getSeasonForChapter(currentChapterId);
  const chapterScenes = useMemo(() => getScenesForChapter(currentChapterId), [currentChapterId]);
  const routedScene = progressApi.progress.route.sceneId;
  const currentSceneId = useMemo<SceneId>(() => {
    if (screen === "quiz") return routedScene === "F02" ? "F02" : "F01";
    if (screen === "report") return "F03";
    if (routedScene && chapterScenes.some((scene) => scene.id === routedScene)) return routedScene;
    return chapterScenes[0]?.id ?? "C1-00";
  }, [chapterScenes, routedScene, screen]);
  const currentScene = getSceneDefinition(currentSceneId);
  const sceneProgress = progressApi.progress.scenes[currentSceneId] ?? createSceneProgress(currentSceneId);
  const natureStageSnapshot = useMemo(
    () => deriveNatureStageSnapshot(currentSceneId, sceneProgress),
    [currentSceneId, sceneProgress]
  );
  const currentScenePosition = chapterScenes.findIndex((scene) => scene.id === currentSceneId);
  const aiThreadId = screen === "quiz" ? "quiz" : currentChapterId;
  const aiMessages = progressApi.progress.aiThreads[aiThreadId] ?? [];

  useEffect(() => {
    lastAssistantTaskEvent.current = null;
  }, [currentSceneId, screen]);
  const activeQuizAssistantContext = screen === "quiz" && quizAssistantContext?.sceneId === currentSceneId
    ? quizAssistantContext
    : null;
  const assistantSnapshot = useMemo(() => createAssistantContextSnapshot({
    sceneProgress,
    currentItemId: activeQuizAssistantContext?.currentItemId,
    assistantControlValues: activeQuizAssistantContext?.controlValues,
    recentMessages: recentDialogue(aiMessages, currentSceneId)
  }), [activeQuizAssistantContext, aiMessages, currentSceneId, sceneProgress]);

  const aiCompanionOpen = activeSurface === "ai";
  const passageOpen = activeSurface === "passage";
  const settingsOpen = activeSurface === "settings";
  const mobileAiModal = aiCompanionOpen && compactViewport;

  const showInteractionFeedback = useCallback((
    kind: InteractionFeedbackKind,
    message: string,
    title?: string
  ) => {
    interactionFeedbackId.current += 1;
    setInteractionStatus({ id: interactionFeedbackId.current, kind, message, title });
  }, []);

  const handleBlockedAction = useCallback((message: string) => {
    playUiSound("incorrect");
    showInteractionFeedback("error", message, "还不能继续");
  }, [showInteractionFeedback]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [screen, currentChapterId, currentSceneId]);

  useEffect(() => {
    setSoundEnabled(progressApi.progress.soundEnabled);
  }, [progressApi.progress.soundEnabled]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const button = event.target instanceof Element ? event.target.closest("button") : null;
      if (button && !button.hasAttribute("disabled")) {
        if (button.matches('[data-progress-ready="false"][data-blocked-feedback="task"]')) return;
        if (screen !== "title") startJourneyAudio();
        playUiSound("tap");
      }
    }
    function handleDragStart(event: DragEvent) {
      if (event.target instanceof Element && event.target.closest("[draggable='true']")) {
        if (screen !== "title") startJourneyAudio();
        playUiSound("pickup");
      }
    }
    window.addEventListener("click", handleClick, true);
    window.addEventListener("dragstart", handleDragStart, true);
    return () => {
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("dragstart", handleDragStart, true);
    };
  }, [screen]);

  useEffect(() => {
    setMusicDucked(activeSurface !== null);
  }, [activeSurface]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const update = () => setCompactViewport(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!mobileAiModal) return;
    const roots = [...document.querySelectorAll<HTMLElement>(".hud, .chapter-shell, .quiz-screen, .nature-stage")];
    const previous = roots.map((element) => ({
      element,
      inert: element.inert,
      ariaHidden: element.getAttribute("aria-hidden")
    }));
    roots.forEach((element) => {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    });
    return () => previous.forEach(({ element, inert, ariaHidden }) => {
      element.inert = inert;
      if (ariaHidden === null) element.removeAttribute("aria-hidden");
      else element.setAttribute("aria-hidden", ariaHidden);
    });
  }, [mobileAiModal, screen]);

  useEffect(() => setActiveSurface(null), [screen, currentChapterId, currentSceneId]);

  useEffect(() => {
    if (currentSceneId !== "C2-00") return;
    setExperimentValue(getSavedTimelinePosition(progressApi.progress.scenes["C2-00"]));
    // Restore only when the routed scene changes. Live drag previews must not
    // be overwritten by the persisted position before the user releases it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSceneId]);

  useEffect(() => {
    if (!interactionStatus) return;
    if (interactionStatus.kind === "error") return;
    const duration = interactionStatus.kind === "success" ? 4800 : 3000;
    const feedbackId = interactionStatus.id;
    let timer = 0;
    const dismissWhenIdle = () => {
      const banner = document.querySelector<HTMLElement>(`[data-feedback-id="${feedbackId}"]`);
      if (banner?.matches(":hover") || banner?.contains(document.activeElement)) {
        timer = window.setTimeout(dismissWhenIdle, 1000);
        return;
      }
      setInteractionStatus((current) => current?.id === feedbackId ? null : current);
    };
    timer = window.setTimeout(dismissWhenIdle, duration);
    return () => window.clearTimeout(timer);
  }, [interactionStatus]);

  function openEvidence(paragraphId: EvidenceId, title?: string) {
    setEvidenceId(paragraphId);
    setEvidenceTitle(title);
    setFocusEvidenceIds([paragraphId]);
    setActiveSurface("evidence");
  }

  function closeEvidence() {
    setEvidenceId(null);
    setActiveSurface((current) => current === "evidence" ? null : current);
  }

  const recordEvidenceViewed = useCallback((evidenceId: EvidenceId, sceneId: SceneId) => {
    progressApi.recordEvidence(evidenceId, sceneId);
  }, [progressApi.recordEvidence]);

  const publishAssistantTaskEvent = useCallback((
    sceneId: SceneId,
    kind: AssistantTaskEventKind,
    signature: string
  ) => {
    const createdAt = Date.now();
    const previous = lastAssistantTaskEvent.current;
    if (previous?.signature === signature && createdAt - previous.createdAt < 300) return;
    lastAssistantTaskEvent.current = { signature, createdAt };
    assistantTaskEventId.current += 1;
    setAssistantTaskEvent({ id: assistantTaskEventId.current, sceneId, kind });
  }, []);

  const recordAttemptWithSound = useCallback((correct: boolean, isFirstTry: boolean) => {
    playUiSound(correct ? "correct" : "incorrect");
    publishAssistantTaskEvent(
      currentSceneId,
      correct ? "success" : "error",
      `${currentSceneId}:legacy:${correct ? "success" : "error"}:${isFirstTry ? "first" : "retry"}`
    );
    progressApi.recordAttempt(correct, isFirstTry);
    progressApi.recordSceneEvent(currentSceneId, {
      kind: "submit",
      actionId: isFirstTry ? "action.task.attempt" : "action.task.retry",
      result: correct ? "success" : "error",
      valueId: correct && !isFirstTry ? "value.corrected" : "value.submitted",
      itemId: getAssistantGuide(currentSceneId).currentItemIds[0],
      misconceptionTag: correct ? undefined : misconceptionForScene(currentSceneId)
    });
  }, [currentSceneId, progressApi.recordAttempt, progressApi.recordSceneEvent, publishAssistantTaskEvent]);

  const recordRuleAttemptWithSound = useCallback<ChapterInteractionProps["onRuleAttempt"]>((sceneId, ruleId, correct, value) => {
    playUiSound(correct ? "correct" : "incorrect");
    publishAssistantTaskEvent(
      sceneId,
      correct ? "success" : "error",
      `${sceneId}:${ruleId}:${correct ? "success" : "error"}:${taskAttemptValueKey(value)}`
    );
    progressApi.recordRuleAttempt(sceneId, ruleId, correct, value, misconceptionForScene(sceneId));
    const feedbackOwnedByInteraction = /^(?:c1\.card|c4\.use)\./u.test(ruleId);
    if (sceneId === currentSceneId && !feedbackOwnedByInteraction && !correct) {
      const c3SummaryMissingEvidence = isC3SummaryMissingEvidence(sceneId, ruleId, sceneProgress.evidenceViews);
      showInteractionFeedback(
        "error",
        c3SummaryMissingEvidence
          ? "请点击各个卡片右上角缩略图，验证课文后作答。"
          : "这项选择不符合本题的条件。已完成的内容会保留，请回看当前提示后重试。",
        c3SummaryMissingEvidence ? "尚未查看课文" : "选择不匹配"
      );
    }
  }, [currentSceneId, progressApi.recordRuleAttempt, publishAssistantTaskEvent, sceneProgress.evidenceViews, showInteractionFeedback]);

  const resetSceneWithAssistantState = useCallback((sceneId: SceneId) => {
    publishAssistantTaskEvent(sceneId, "reset", `${sceneId}:reset`);
    progressApi.resetSceneLayout(sceneId);
  }, [progressApi.resetSceneLayout, publishAssistantTaskEvent]);

  const patchVisualStateWithAssistant = useCallback<ChapterInteractionProps["onVisualState"]>((sceneId, patch) => {
    progressApi.patchSceneVisualState(sceneId, patch);
  }, [progressApi.patchSceneVisualState]);

  function openMap() {
    setMapCompletionNotice(null);
    setActiveSurface(null);
    setInteractionStatus(null);
    setScreen("map");
    progressApi.setRoute("map", currentChapterId, null);
  }

  function openChapter(chapterId: ChapterId) {
    setMapCompletionNotice(null);
    const scenes = getScenesForChapter(chapterId);
    const saved = progressApi.progress.route.chapterId === chapterId
      ? progressApi.progress.route.sceneId
      : null;
    const target = saved && scenes.some((scene) => scene.id === saved)
      ? saved
      : scenes.find((scene) => progressApi.progress.scenes[scene.id]?.status !== "mastered")?.id ?? scenes[0].id;
    setActiveSurface(null);
    setInteractionStatus(null);
    setCurrentChapterId(chapterId);
    setScreen("chapter");
    progressApi.startScene(target, chapterId);
  }

  function changeScene(sceneId: SceneId) {
    setActiveSurface(null);
    setInteractionStatus(null);
    setScreen("chapter");
    progressApi.startScene(sceneId, currentChapterId);
  }

  function completeCurrentChapter() {
    playUiSound("complete");
    progressApi.completeChapter(currentChapterId);
    const chapterIndex = chapters.findIndex((chapter) => chapter.id === currentChapterId);
    const nextChapter = chapters[chapterIndex + 1];
    setMapCompletionNotice({
      chapterId: currentChapterId,
      message: nextChapter
        ? `“${currentChapter.title}”已完成，得分和观察记录均已保存；“${nextChapter.title}”现已解锁。`
        : `“${currentChapter.title}”已完成，得分和观察记录均已保存；结课核验现已开放。`
    });
    setActiveSurface(null);
    setInteractionStatus(null);
    setScreen("map");
    progressApi.setRoute("map", currentChapterId, null);
  }

  const chapterProps = useMemo<ChapterInteractionProps>(() => ({
    sceneId: currentSceneId,
    sceneProgress,
    onSceneChange: changeScene,
    onSceneStatus: progressApi.setSceneStatus,
    onCompleteScene: progressApi.completeScene,
    onRuleAttempt: recordRuleAttemptWithSound,
    onAttempt: recordAttemptWithSound,
    onEvidence: openEvidence,
    onEvidenceViewed: recordEvidenceViewed,
    onVisualState: patchVisualStateWithAssistant,
    onResetScene: resetSceneWithAssistantState,
    onExperimentChange: setExperimentValue,
    onComplete: completeCurrentChapter
  }), [currentSceneId, sceneProgress, progressApi, patchVisualStateWithAssistant, recordAttemptWithSound, recordEvidenceViewed, recordRuleAttemptWithSound, resetSceneWithAssistantState]);

  function renderChapter() {
    if (currentChapterId === "chapter-1") return <ChapterOne {...chapterProps} />;
    if (currentChapterId === "chapter-2") return <ChapterTwo {...chapterProps} />;
    if (currentChapterId === "chapter-3") return <ChapterThree {...chapterProps} />;
    return <ChapterFour {...chapterProps} />;
  }

  function resetExperience() {
    progressApi.resetProgress();
    window.location.reload();
  }

  function openQuiz() {
    setActiveSurface(null);
    setInteractionStatus(null);
    setQuizAssistantContext(null);
    setScreen("quiz");
    progressApi.startScene("F01", currentChapterId);
  }

  function finishQuiz(firstScore: number, masteryScore: number) {
    playUiSound("complete");
    setQuizAssistantContext(null);
    progressApi.saveQuiz(firstScore, masteryScore);
    progressApi.setRoute("report", currentChapterId, "F03");
    setInteractionStatus(null);
    setScreen("report");
  }

  const handleQuizAssistantContextChange = useCallback((context: QuizAssistantContext) => {
    setQuizAssistantContext(context);
    progressApi.recordSceneEvent(context.sceneId, {
      kind: "select",
      actionId: "action.item.select",
      result: "pending",
      valueId: "value.selected",
      itemId: context.currentItemId
    });
    if (context.sceneId === "F02" && progressApi.progress.route.sceneId !== "F02") {
      progressApi.completeScene("F01");
      progressApi.startScene("F02", currentChapterId);
    }
  }, [currentChapterId, progressApi.completeScene, progressApi.progress.route.sceneId, progressApi.recordSceneEvent, progressApi.startScene]);

  const handleAuditComplete = useCallback((ids: EvidenceId[]) => {
    progressApi.setAiAuditCompleted(true);
    progressApi.setSelectedEvidenceIds("F02", ids);
    progressApi.completeScene("F02");
  }, [progressApi.completeScene, progressApi.setAiAuditCompleted, progressApi.setSelectedEvidenceIds]);

  return (
    <div
      className={`app app--${screen} app--surface-${activeSurface ?? "closed"} ${aiCompanionOpen ? "app--ai-open" : ""} ${progressApi.progress.reducedMotion ? "reduce-motion" : ""}`}
      data-mobile-ai-modal={mobileAiModal ? "true" : "false"}
      data-scene-id={screen === "chapter" ? currentSceneId : undefined}
    >
      {screen === "chapter" && webGlAvailable ? (
        <RendererErrorBoundary
          key={rendererRevision}
          fallback={(
            <StaticBackdrop
              season={season}
              snapshot={natureStageSnapshot}
              notice="3D 场景加载失败。当前任务和已完成的进度不受影响，可以继续观察和作答。"
              onRetry={() => setRendererRevision((value) => value + 1)}
            />
          )}
        >
          <Suspense fallback={<StaticBackdrop season={season} snapshot={natureStageSnapshot} />}>
            <NatureStage
              screen={screen}
              chapterId={currentChapterId}
              sceneId={currentSceneId}
              sceneProgress={sceneProgress}
              season={season}
              experimentValue={experimentValue}
              reducedMotion={progressApi.progress.reducedMotion}
              quality={progressApi.progress.quality}
              controlsEnabled={activeSurface === null || (activeSurface === "ai" && !compactViewport)}
            />
          </Suspense>
        </RendererErrorBoundary>
      ) : screen === "quiz" ? (
        <div className="quiz-paper-backdrop" aria-hidden="true" />
      ) : (
        <StaticBackdrop
          season={season}
          snapshot={natureStageSnapshot}
          notice={screen === "chapter" && !webGlAvailable
            ? "当前浏览器无法显示 3D 场景。所有观察和判断任务仍可正常完成。"
            : undefined}
        />
      )}
      <div className="paper-grain" aria-hidden="true" />
      <div className="paper-transition-art" aria-hidden="true">
        <AssetImage assetId="1B-01" className="paper-transition-art__texture" alt="" sizes="100vw" />
        <AssetImage assetId="1B-02" className="paper-transition-art__height" alt="" sizes="100vw" />
        <AssetImage assetId="1B-03" className="paper-transition-art__edge paper-transition-art__edge--left" alt="" sizes="35vw" />
        <AssetImage assetId="1B-04" className="paper-transition-art__edge paper-transition-art__edge--right" alt="" sizes="35vw" />
        <AssetImage assetId="1B-05" className="paper-transition-art__edge paper-transition-art__edge--bottom" alt="" sizes="55vw" />
      </div>

      {screen !== "title" && <Hud
        title={screen === "chapter" ? currentChapter.title : screen === "map" ? "物候观察旅程" : screen === "quiz" ? "结课核验" : "能力档案"}
        chapterLabel={screen === "chapter" ? `第${currentChapter.number}章` : undefined}
        stepLabel={screen === "chapter" ? currentScene.title : undefined}
        stepCurrent={screen === "chapter" ? currentScenePosition + 1 : undefined}
        stepTotal={screen === "chapter" ? chapterScenes.length : undefined}
        soundEnabled={progressApi.progress.soundEnabled}
        onToggleSound={() => {
          const enabled = !progressApi.progress.soundEnabled;
          setSoundEnabled(enabled);
          progressApi.setSoundEnabled(enabled);
          if (enabled) playUiSound("correct", true);
        }}
        onOpenPassage={() => setActiveSurface("passage")}
        onOpenSettings={() => setActiveSurface("settings")}
        onOpenMap={openMap}
        showPassage={screen === "chapter"}
      />}

      {screen === "title" && <TitleScreen onStart={() => { startJourneyAudio(); setScreen("map"); progressApi.setRoute("map", currentChapterId, null); }} />}
      {screen === "map" && <JourneyMap progress={progressApi.progress} onOpenChapter={openChapter} onOpenQuiz={openQuiz} completionNotice={mapCompletionNotice} />}
      {screen === "chapter" && (
        <InteractionFeedbackProvider
          feedback={interactionStatus}
          onDismiss={() => setInteractionStatus(null)}
          onBlocked={handleBlockedAction}
        >
          <main className="chapter-shell" key={currentChapterId}>
            <Suspense fallback={<div className="chapter-panel"><span className="section-kicker">纸层展开中</span><h1>正在准备观察台……</h1></div>}>{renderChapter()}</Suspense>
          </main>
        </InteractionFeedbackProvider>
      )}
      {screen === "quiz" && <QuizScreen
        onAttempt={recordAttemptWithSound}
        onEvidence={(id, title) => {
          const evidence = id as EvidenceId;
          recordEvidenceViewed(evidence, currentSceneId);
          openEvidence(evidence, title);
        }}
        onEvidenceViewed={() => undefined}
        onAssistantContextChange={handleQuizAssistantContextChange}
        onQuestionResult={progressApi.saveQuizAnswer}
        onAuditComplete={handleAuditComplete}
        initialQuiz={progressApi.progress.quiz}
        initialAuditCompleted={progressApi.progress.aiAuditCompleted}
        onFinish={finishQuiz}
      />}
      {screen === "report" && <ReportScreen progress={progressApi.progress} onOpenMap={openMap} onRestart={() => { setScreen("title"); progressApi.setRoute("title", currentChapterId, null); }} />}

      {(screen === "chapter" || screen === "quiz") && <AiCompanion
        open={aiCompanionOpen}
        threadId={aiThreadId}
        chapterId={screen === "quiz" ? "quiz" : currentChapterId}
        snapshot={assistantSnapshot}
        aiMode={progressApi.progress.aiMode}
        messages={aiMessages}
        introAcknowledged={progressApi.progress.aiIntroAcknowledged}
        taskEvent={assistantTaskEvent}
        onHintUsed={() => progressApi.recordHint(currentSceneId)}
        onIntroAcknowledged={progressApi.acknowledgeAiIntro}
        onEvidence={openEvidence}
        onEvidenceViewed={recordEvidenceViewed}
        onThreadChange={progressApi.setAiThread}
        onOpenChange={(open) => setActiveSurface(open ? "ai" : null)}
      />}

      <PassageDrawer open={passageOpen} chapterId={currentChapterId} focusIds={focusEvidenceIds} onClose={() => setActiveSurface(null)} onEvidenceViewed={(id) => recordEvidenceViewed(id, currentSceneId)} />
      <EvidenceSheet paragraphId={activeSurface === "evidence" ? evidenceId : null} title={evidenceTitle} onClose={closeEvidence} />
      <SettingsDialog open={settingsOpen} reducedMotion={progressApi.progress.reducedMotion} quality={progressApi.progress.quality} aiMode={progressApi.progress.aiMode} onReducedMotionChange={progressApi.setReducedMotion} onQualityChange={progressApi.setQuality} onAiModeChange={progressApi.setAiMode} onReset={resetExperience} onClose={() => setActiveSurface(null)} />
    </div>
  );
}
